import React, { useMemo, useState } from 'react';
import { BarChart3Icon, CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, ImageIcon, MailIcon, MegaphoneIcon, MessagesSquareIcon, SearchIcon, XIcon } from 'lucide-react';
import { siFacebook, siInstagram, siLinkedin, siPinterest, siTiktok, siYoutube } from 'simple-icons/icons';
import { owners } from './RecoveryPortal';
type PlatformType = 'ads' | 'social' | 'lifecycle';
type Platform = {
  name: string;
  type: PlatformType;
  iconPath?: string;
  iconUrl?: string;
  connected: boolean;
  spend?: string;
  creative?: string;
};
type Campaign = {
  name: string;
  brand: string;
  platform: string;
  window: string;
  spend: string;
  revenue: string;
  roas: string;
  units: string;
  status: 'Active' | 'Scheduled' | 'Completed';
};
const initialPlatforms: Platform[] = [{
  name: 'Google Ads',
  type: 'ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/7Rcmt8YbLjZHpSjzxgNXp2/google.svg',
  connected: true,
  spend: '$4,280',
  creative: '4 active assets'
}, {
  name: 'Facebook Ads',
  type: 'ads',
  iconPath: siFacebook.path,
  connected: true,
  spend: '$3,840',
  creative: '6 active assets'
}, {
  name: 'Instagram Ads',
  type: 'ads',
  iconPath: siInstagram.path,
  connected: true,
  spend: '$2,960',
  creative: '5 active assets'
}, {
  name: 'Vibe.co',
  type: 'ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/4QsPQBQikeUopo55GPTpMC/vibe.svg',
  connected: false
}, {
  name: 'MNTN',
  type: 'ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/81pvkRmDnW9HU5AzT3w7th/mntn.svg',
  connected: false
}, {
  name: 'Pinterest Ads',
  type: 'ads',
  iconPath: siPinterest.path,
  connected: false
}, {
  name: 'TikTok Ads',
  type: 'ads',
  iconPath: siTiktok.path,
  connected: false
}, {
  name: 'Facebook',
  type: 'social',
  iconPath: siFacebook.path,
  connected: true,
  creative: '12 posts this month'
}, {
  name: 'Instagram',
  type: 'social',
  iconPath: siInstagram.path,
  connected: true,
  creative: '18 posts this month'
}, {
  name: 'YouTube',
  type: 'social',
  iconPath: siYoutube.path,
  connected: false
}, {
  name: 'Pinterest',
  type: 'social',
  iconPath: siPinterest.path,
  connected: false
}, {
  name: 'LinkedIn',
  type: 'social',
  iconPath: siLinkedin.path,
  connected: false
}, {
  name: 'TikTok',
  type: 'social',
  iconPath: siTiktok.path,
  connected: false
}, {
  name: 'Klaviyo',
  type: 'lifecycle',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/3rX7mUrbpBP38ioN5pJ69R/Klaviyo.svg',
  connected: true,
  creative: '8 flows live'
}];
const campaigns: Campaign[] = [{
  name: 'Spring Collection — Jackets',
  brand: 'Eddy',
  platform: 'Google Ads',
  window: 'Jun 2 – Jul 14',
  spend: '$1,840',
  revenue: '$9,420',
  roas: '5.1x',
  units: '96',
  status: 'Active'
}, {
  name: 'Cardigan search intent',
  brand: 'Eddy',
  platform: 'Google Ads',
  window: 'May 18 – Jun 30',
  spend: '$1,260',
  revenue: '$5,180',
  roas: '4.1x',
  units: '61',
  status: 'Active'
}, {
  name: 'Recovery edit — shopping',
  brand: 'Eddy',
  platform: 'Google Ads',
  window: 'Apr 6 – May 12',
  spend: '$1,180',
  revenue: '$4,020',
  roas: '3.4x',
  units: '48',
  status: 'Completed'
}, {
  name: 'Everyday essentials',
  brand: '1 People',
  platform: 'Google Ads',
  window: 'Jun 10 – Jul 20',
  spend: '$960',
  revenue: '$3,410',
  roas: '3.6x',
  units: '39',
  status: 'Active'
}, {
  name: 'Summer occasion edit',
  brand: 'Eddy',
  platform: 'Facebook Ads',
  window: 'Jul 1 – Aug 18',
  spend: '$2,140',
  revenue: '$8,260',
  roas: '3.9x',
  units: '88',
  status: 'Active'
}, {
  name: 'Coordinated sets test',
  brand: 'Eddy',
  platform: 'Facebook Ads',
  window: 'Aug 1 – Sep 5',
  spend: '$1,120',
  revenue: '—',
  roas: '—',
  units: '—',
  status: 'Scheduled'
}, {
  name: 'Second-life storytelling',
  brand: 'Eddy',
  platform: 'Instagram Ads',
  window: 'Jun 15 – Jul 30',
  spend: '$1,860',
  revenue: '$7,140',
  roas: '3.8x',
  units: '74',
  status: 'Active'
}, {
  name: 'Plus-size spotlight',
  brand: '1 People',
  platform: 'Instagram Ads',
  window: 'May 4 – Jun 8',
  spend: '$740',
  revenue: '$2,180',
  roas: '2.9x',
  units: '24',
  status: 'Completed'
}, {
  name: 'Manymoons partnership push',
  brand: 'Eddy',
  platform: 'Instagram',
  window: 'Monthly',
  spend: '—',
  revenue: '$3,960',
  roas: '—',
  units: '41',
  status: 'Active'
}, {
  name: 'Behind the collection',
  brand: 'Nature Baby',
  platform: 'Facebook',
  window: 'Jun 20 – Jul 20',
  spend: '—',
  revenue: '$1,240',
  roas: '—',
  units: '15',
  status: 'Active'
}, {
  name: 'Recovery drop announcement',
  brand: 'Eddy',
  platform: 'Klaviyo',
  window: 'Jun 4',
  spend: '$180',
  revenue: '$6,480',
  roas: '36x',
  units: '72',
  status: 'Completed'
}, {
  name: 'Abandoned browse flow',
  brand: 'Eddy',
  platform: 'Klaviyo',
  window: 'Always on',
  spend: '$240',
  revenue: '$8,910',
  roas: '37x',
  units: '104',
  status: 'Active'
}, {
  name: 'Winback — lapsed buyers',
  brand: '1 People',
  platform: 'Klaviyo',
  window: 'Always on',
  spend: '$160',
  revenue: '$3,240',
  roas: '20x',
  units: '38',
  status: 'Active'
}];
export function DemandPlatformConnections() {
  const [platforms, setPlatforms] = useState(initialPlatforms);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [brand, setBrand] = useState('All brands');
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandQuery, setBrandQuery] = useState('');
  const [adsOpen, setAdsOpen] = useState(true);
  const [socialOpen, setSocialOpen] = useState(true);
  const [lifecycleOpen, setLifecycleOpen] = useState(true);
  const ads = platforms.filter(platform => platform.type === 'ads');
  const social = platforms.filter(platform => platform.type === 'social');
  const lifecycle = platforms.filter(platform => platform.type === 'lifecycle');
  const filteredBrands = useMemo(() => owners.filter(owner => owner.toLocaleLowerCase().includes(brandQuery.trim().toLocaleLowerCase())), [brandQuery]);
  const platformCampaigns = useMemo(() => campaigns.filter(campaign => campaign.platform === selectedPlatform?.name && (brand === 'All brands' || campaign.brand === brand)), [selectedPlatform, brand]);
  const toggleConnection = (platform: Platform) => {
    setPlatforms(current => current.map(item => item.name === platform.name && item.type === platform.type ? {
      ...item,
      connected: !item.connected
    } : item));
    setSelectedPlatform(current => current ? {
      ...current,
      connected: !current.connected
    } : null);
  };
  return <section className="mt-6" aria-label="Campaign and social platforms" data-id="element-871" data-mp="1k3hw5t6saptr">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-872" data-mp="1p4p4btcu9r0y">
        <div data-id="element-873" data-mp="2cut6rxeajkqd">
          <h2 className="text-lg font-semibold" data-id="element-874" data-mp="2crx641b26hy8">Channel connections</h2>
          <p className="mt-1 text-xs text-ink/55" data-id="element-875" data-mp="2hrrwm3qx95ir">Select a channel to review the campaigns running against your inventory.</p>
        </div>
        <div className="relative w-full sm:w-56" data-id="element-876" data-mp="23vobr11xu9iy">
          <button type="button" onClick={() => {
          setBrandOpen(open => !open);
          setBrandQuery('');
        }} aria-expanded={brandOpen} aria-controls="platform-brand-options" className="flex w-full items-center justify-between gap-2 rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-left text-sm font-semibold text-ink transition-colors hover:border-[#BB5E40]/35 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-877" data-mp="27tlyzyc5eljp">
            <span className="truncate" data-id="element-878" data-mp="ytdmvd5rbkd9">{brand}</span>
            <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${brandOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-879" data-mp="ys2et15a3qgc" />
          </button>
          {brandOpen && <div id="platform-brand-options" role="listbox" aria-label="Filter campaigns by brand" className="absolute right-0 z-20 mt-2 w-full overflow-hidden rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg" data-id="element-880" data-mp="63eqewp8gkyk">
              <div className="relative mb-1.5" data-id="element-881" data-mp="b544ymbnzgre">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-882" data-mp="b39j95skjkm9" />
                <label htmlFor="platform-brand-search" className="sr-only" data-id="element-883" data-mp="3elt0zedmf82t">Search brands</label>
                <input id="platform-brand-search" type="search" value={brandQuery} onChange={event => setBrandQuery(event.target.value)} placeholder="Search brands" autoFocus className="w-full rounded-lg border border-ink/10 bg-cream/55 py-2 pl-8 pr-3 text-sm text-ink outline-none placeholder:text-ink/40 focus:border-[#BB5E40]/35" data-id="element-884" data-mp="2t2830hc2lpid" />
              </div>
              <div className="max-h-48 overflow-y-auto" data-id="element-885" data-mp="3h06zj5cixw0h">
                <button type="button" role="option" aria-selected={brand === 'All brands'} onClick={() => {
              setBrand('All brands');
              setBrandQuery('');
              setBrandOpen(false);
            }} className={`flex w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${brand === 'All brands' ? 'bg-[#BB5E40] text-cream' : 'text-ink/70 hover:bg-cream hover:text-ink'}`} data-id="element-886" data-mp="3gym6jk5uyl4q">
                  All brands
                </button>
                {filteredBrands.map(item => <button key={item} type="button" role="option" aria-selected={brand === item} onClick={() => {
              setBrand(item);
              setBrandQuery('');
              setBrandOpen(false);
            }} className={`flex w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${brand === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/70 hover:bg-cream hover:text-ink'}`} data-id="element-887" data-mp="1j6cktep8qujt">
                    {item}
                  </button>)}
                {filteredBrands.length === 0 && brandQuery.trim() && <p className="px-3 py-4 text-center text-sm text-ink/50" data-id="element-888" data-mp="2qj4o0c17xrf3">No brands found.</p>}
              </div>
            </div>}
        </div>
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-2" data-id="element-889" data-mp="2lp00522vxrxz">
        <PlatformSection title="Ads" icon={MegaphoneIcon} description="Paid media channels for campaign spend, creative, and performance." platforms={ads} open={adsOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setAdsOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-890" data-mp="2gpwrz8rlz5ls" />
        <PlatformSection title="Social" icon={MessagesSquareIcon} description="Publishing and content channels for organic reach and engagement." platforms={social} open={socialOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setSocialOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-891" data-mp="34maohrmzb0yn" />
        <PlatformSection title="Email & SMS" icon={MailIcon} description="Lifecycle channels for owned audience campaigns and automated flows." platforms={lifecycle} open={lifecycleOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setLifecycleOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-892" data-mp="39eu9h9nzwydh" />
      </div>

      {selectedPlatform && <PlatformDetail platform={selectedPlatform} brand={brand} campaigns={platformCampaigns} onClose={() => setSelectedPlatform(null)} onToggle={() => toggleConnection(selectedPlatform)} data-id="element-893" data-mp="atpngpszogzd" />}
    </section>;
}
function PlatformSection({
  title,
  icon: Icon,
  description,
  platforms,
  open,
  selectedName,
  onToggle,
  onSelect
}: {
  title: string;
  icon: typeof MegaphoneIcon;
  description: string;
  platforms: Platform[];
  open: boolean;
  selectedName?: string;
  onToggle: () => void;
  onSelect: (platform: Platform) => void;
}) {
  const connected = platforms.filter(platform => platform.connected).length;
  return <article className={`rounded-3xl border border-ink/10 bg-paper shadow-sm ${open ? 'p-5 sm:p-6' : 'px-5 py-4 sm:px-6'}`} data-id="element-894" data-mp="30ax2vprt01ci">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 text-left" data-id="element-895" data-mp="359qhjlittye9"><div className="flex items-center gap-2" data-id="element-896" data-mp="1avw5lfe2dps1"><Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-897" data-mp="1632y4jr7o3c9" /><h3 className="text-lg font-semibold" data-id="element-898" data-mp="161i3yv8t6mex">{title}</h3></div><span className="flex items-center gap-2 text-xs font-semibold text-forest" data-id="element-899" data-mp="1151gm39foc2w">{connected} connected{open ? <ChevronDownIcon className="h-4 w-4 text-ink/45" data-id="element-900" data-mp="x3xz7cbqm7zk" /> : <ChevronRightIcon className="h-4 w-4 text-ink/45" data-id="element-901" data-mp="1tsnk7binvgpr" />}</span></button>
      {open && <><p className="mt-2 text-xs leading-relaxed text-ink/55" data-id="element-902" data-mp="1tqjf4q910bnw">{description}</p><div className="mt-5 grid grid-cols-3 gap-2 sm:grid-cols-4" data-id="element-903" data-mp="1oum14ql8jhea">{platforms.map(platform => <PlatformTile key={platform.name} platform={platform} selected={selectedName === platform.name} onClick={() => onSelect(platform)} data-id="element-904" data-mp="1kqo1mes004kp" />)}</div></>}
    </article>;
}
function PlatformTile({
  platform,
  selected,
  onClick
}: {
  platform: Platform;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} aria-label={`Open ${platform.name} ${platform.type} workspace`} className={`relative flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-xl border p-2 text-center transition-colors ${selected ? 'border-[#BB5E40] bg-[#BB5E40]/8' : platform.connected ? 'border-forest/25 bg-forest/[0.035] hover:border-forest/45' : 'border-ink/10 bg-cream/40 hover:border-[#BB5E40]/35 hover:bg-[#BB5E40]/[0.035]'}`} data-id="element-905" data-mp="4ceefnzhslvg">
      <PlatformMark platform={platform} data-id="element-906" data-mp="2yqr9bgt40jij" />
      <span className="max-w-full truncate text-[11px] font-semibold leading-none text-ink/75" data-id="element-907" data-mp="2yu6mge383ihb">{platform.name}</span>
      <span className={`absolute right-2 top-2 h-1.5 w-1.5 rounded-full ${platform.connected ? 'bg-forest' : 'bg-ink/20'}`} aria-hidden="true" data-id="element-908" data-mp="38q9nthq93d37" />
    </button>;
}
function PlatformDetail({
  platform,
  brand,
  campaigns: platformCampaigns,
  onClose,
  onToggle
}: {
  platform: Platform;
  brand: string;
  campaigns: Campaign[];
  onClose: () => void;
  onToggle: () => void;
}) {
  const totalSpend = platformCampaigns.reduce((sum, campaign) => sum + Number(campaign.spend.replace(/[^0-9.]/g, '') || 0), 0);
  const totalRevenue = platformCampaigns.reduce((sum, campaign) => sum + Number(campaign.revenue.replace(/[^0-9.]/g, '') || 0), 0);
  return <article className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-live="polite" data-id="element-909" data-mp="x0io3coh02x3">
      <div className="flex items-start justify-between gap-4" data-id="element-910" data-mp="s30c1df5p7kv">
        <div className="flex items-center gap-3" data-id="element-911" data-mp="4cd2knple9w7">
          <PlatformMark platform={platform} data-id="element-912" data-mp="4d6336xyk35w" />
          <div data-id="element-913" data-mp="3vjpjd1mkclbl">
            <h3 className="text-lg font-semibold" data-id="element-914" data-mp="3vk8sbsu8jzdh">{platform.name}</h3>
            <p className="mt-1 text-xs text-ink/55" data-id="element-915" data-mp="e2ftmid5ldrw">{brand} · {platform.type === 'ads' ? 'Paid campaign workspace' : platform.type === 'lifecycle' ? 'Email and SMS workspace' : 'Social publishing workspace'}</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-ink/45 hover:bg-cream hover:text-ink" aria-label="Close platform details" data-id="element-916" data-mp="3hqhqz7qnrlsu"><XIcon className="h-4 w-4" data-id="element-917" data-mp="3cqvbssr9rvhe" /></button>
      </div>

      {platform.connected ? <>
          <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-918" data-mp="1x1do0xfcs1f4">
            <DetailMetric icon={BarChart3Icon} label={platform.type === 'social' ? 'Campaigns running' : 'Spend'} value={platform.type === 'social' ? `${platformCampaigns.length}` : formatCurrency(totalSpend)} data-id="element-919" data-mp="1x2yikn1o3kcz" />
            <DetailMetric icon={CheckCircle2Icon} label="Attributed revenue" value={formatCurrency(totalRevenue)} data-id="element-920" data-mp="1nbu55rrn47k9" />
            <DetailMetric icon={ImageIcon} label={platform.type === 'lifecycle' ? 'Automations' : 'Creative'} value={platform.creative ?? 'No assets yet'} data-id="element-921" data-mp="2azc3k65eu3w7" />
          </div>

          <div className="mt-5" data-id="element-922" data-mp="21zytpcddjday">
            <h4 className="text-sm font-semibold" data-id="element-923" data-mp="26zjqbg52n9ki">{platform.name} campaigns · {brand}</h4>
            {platformCampaigns.length > 0 ? <div className="mt-3 -mx-2 overflow-x-auto px-2" data-id="element-924" data-mp="qka2g8t37s4c">
                <table className="w-full min-w-[640px] text-left text-xs" data-id="element-925" data-mp="vkeijxq32ku1">
                  <thead className="border-b border-ink/10 text-ink/50" data-id="element-926" data-mp="vibmox291xhe">
                    <tr data-id="element-927" data-mp="2x92c1c32wgez">
                      <th scope="col" className="pb-2 font-semibold" data-id="element-928" data-mp="2xbog31qrgdsg">Campaign</th>
                      <th scope="col" className="pb-2 font-semibold" data-id="element-929" data-mp="2sb97enhfbugt">Brand</th>
                      <th scope="col" className="pb-2 font-semibold" data-id="element-930" data-mp="2nig2nzlygm4q">Window</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-931" data-mp="2jiyvn1qvrgxg">Spend</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-932" data-mp="2ejlyno6aoth5">Revenue</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-933" data-mp="29k9a8yzrzecg">ROAS</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-934" data-mp="24kwroqfxstfo">Units</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/10" data-id="element-935" data-mp="20kcszifpthrf">
                    {platformCampaigns.map(campaign => <tr key={`${campaign.platform}-${campaign.name}`} data-id="element-936" data-mp="2t06sq7vq788">
                        <th scope="row" className="py-3 pr-3 text-left font-medium text-ink" data-id="element-937" data-mp="3uydte7qwyctm">
                          <span className="flex items-center gap-2" data-id="element-938" data-mp="1goi9c5394eoo">
                            {campaign.name}
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${campaign.status === 'Active' ? 'bg-forest/10 text-forest' : campaign.status === 'Scheduled' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-ink/7 text-ink/55'}`} data-id="element-939" data-mp="1lnd13wj75dkx">{campaign.status}</span>
                          </span>
                        </th>
                        <td className="py-3 pr-3 text-ink/70" data-id="element-940" data-mp="12qlbz6gz448y">{campaign.brand}</td>
                        <td className="py-3 pr-3 whitespace-nowrap text-ink/60" data-id="element-941" data-mp="16r428w37x291">{campaign.window}</td>
                        <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-942" data-mp="1brjgdpvbqds2">{campaign.spend}</td>
                        <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-943" data-mp="2i5isb1lcuz4s">{campaign.revenue}</td>
                        <td className="py-3 text-right tabular-nums text-forest" data-id="element-944" data-mp="2n42e91zy8ax5">{campaign.roas}</td>
                        <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-945" data-mp="1ytl9bfysyr73">{campaign.units}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <p className="mt-3 rounded-xl bg-cream/60 px-4 py-6 text-center text-sm text-ink/55" data-id="element-946" data-mp="27n7zmqk8r28w">
                No {platform.name} campaigns for {brand} yet.
              </p>}
          </div>
        </> : <div className="mt-5 flex flex-col gap-3 rounded-2xl bg-cream p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-947" data-mp="2ckzx003nngkh">
          <p className="text-sm leading-relaxed text-ink/65" data-id="element-948" data-mp="2qiy7uvirouv8">Connect {platform.name} to bring its {platform.type === 'ads' ? 'campaign spend, creative, and result' : platform.type === 'lifecycle' ? 'email, SMS, and audience' : 'publishing and engagement'} data into Carli for Demand.</p>
          <button type="button" onClick={onToggle} className="shrink-0 rounded-full bg-[#BB5E40] px-4 py-2.5 text-xs font-semibold text-cream hover:bg-[#94452E]" data-id="element-949" data-mp="s5ogf93wyg16">Connect platform</button>
        </div>}
    </article>;
}
function DetailMetric({
  icon: Icon,
  label,
  value
}: {
  icon: typeof BarChart3Icon;
  label: string;
  value: string;
}) {
  return <div className="rounded-xl border border-ink/10 bg-cream/45 p-3" data-id="element-950" data-mp="892a5aftypka"><Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-951" data-mp="d8olfec3lvqg" /><p className="mt-3 text-xs text-ink/50" data-id="element-952" data-mp="3uid1mlos85fr">{label}</p><p className="mt-1 text-sm font-semibold" data-id="element-953" data-mp="3ugjtf1yitd5n">{value}</p></div>;
}
function PlatformMark({
  platform
}: {
  platform: Platform;
}) {
  return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] p-2 text-cream" data-id="element-954" data-mp="3lhetbr7b9brd">{platform.iconUrl ? <img src={platform.iconUrl} alt="" className="h-full w-full object-contain" data-id="element-955" data-mp="3qfq2s0tp1k92" /> : <svg viewBox="0 0 24 24" aria-hidden="true" className="h-full w-full fill-current" data-id="element-956" data-mp="1jqbgqwdv1n46"><path d={platform.iconPath} data-id="element-957" data-mp="1jo7h4qmzva9p" /></svg>}</span>;
}
function formatCurrency(value: number) {
  return value > 0 ? value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }) : '—';
}